'use client'

import ElectionBannerTop from '@page/election/_components/ElectionBanner/ElectionBannerTop'
import { ElectionMetaResponseVo } from '@schemas/non-auth'

import { pxToRem } from '@ui/style/muiTheme'
import { Box } from '@mui/joy'
import UIText from '@components/ui/UIText'
import color from '@ui/style/color.theme'
import UIFlex from '@components/ui/UIFlex'
import DimdLayer from './DimdLayer'

interface BallotBannerProps {
	data: ElectionMetaResponseVo
	viewPoint: string
}

const BallotBanner = ({ data, viewPoint }: BallotBannerProps) => {
	return (
		<>
			{data.topBannerChart.ballotChart.isError && <DimdLayer />}
			<ElectionBannerTop data={data} />
			<UIFlex
				flexDirection="column"
				alignItems="center"
				gap={pxToRem(8)}
				sx={{
					marginTop: {
						xs: pxToRem(81),
						md: pxToRem(19),
						lg: pxToRem(16),
						xl: pxToRem(42),
					},
					display: {
						xs: viewPoint === 'article' ? 'none' : 'flex',
						md: 'flex',
					},
				}}
			>
				<UIText
					sx={{
						fontSize: { xs: pxToRem(28), lg: pxToRem(36) },
						lineHeight: { xs: pxToRem(28), lg: pxToRem(36) },
						color: '#082B4F',
						fontWeight: 800,
					}}
				>
					이 시각 전국 투표율
				</UIText>
				<UIText
					sx={{
						fontSize: { xs: pxToRem(14), lg: pxToRem(16) },
						lineHeight: { xs: pxToRem(14), lg: pxToRem(16) },
						color: '#082B4F',
					}}
				>
					{data.topBannerChart.ballotChart.referenceTime} 기준
				</UIText>
			</UIFlex>
			<Box
				sx={{
					marginTop: {
						xs: viewPoint === 'article' ? pxToRem(98) : pxToRem(92),
						md: pxToRem(53),
						lg: pxToRem(62),
						xl: pxToRem(70),
					},
				}}
			>
				<Box
					sx={{
						position: 'relative',
						width: '100%',
						height: {
							xs: pxToRem(60),
							lg: pxToRem(84),
						},
						backgroundColor: '#C7DCDC',
					}}
				>
					<UIText
						sx={{
							position: 'absolute',
							left: '50%',
							top: pxToRem(-16),
							transform: 'translateX(-50%)',
							zIndex: 2,
							fontSize: pxToRem(12),
							lineHeight: pxToRem(12),
							color: '#097A96',
							'&::after': {
								content: '""',
								position: 'absolute',
								left: '50%',
								top: pxToRem(16),
								transform: 'translateX(-50%)',
								width: pxToRem(1),
								height: {
									xs: pxToRem(60),
									lg: pxToRem(84),
								},
								backgroundImage: 'url(/images/election/banner/dotted_line.png)',
								backgroundSize: 'cover',
							},
						}}
					>
						50%
					</UIText>
					<Box
						sx={{
							width: `${data.topBannerChart.ballotChart.nowBallotRate}%`,
							height: {
								xs: pxToRem(60),
								lg: pxToRem(84),
							},
							backgroundColor: '#0CA6CC',
							position: 'absolute',
							left: 0,
							top: 0,
						}}
					/>
					<UIText
						sx={{
							position: 'absolute',
							left: {
								xs: pxToRem(20),
								lg: pxToRem(32),
							},
							top: {
								xs: pxToRem(14),
								lg: pxToRem(18),
							},
							fontSize: {
								xs: pxToRem(32),
								lg: pxToRem(48),
							},
							lineHeight: {
								xs: pxToRem(32),
								lg: pxToRem(48),
							},
							color: color.colWhite,
							fontWeight: 700,
						}}
					>
						{data.topBannerChart.ballotChart.nowBallotRate}
						<UIText
							sx={{
								fontSize: {
									xs: pxToRem(16),
									lg: pxToRem(24),
								},
								lineHeight: {
									xs: pxToRem(22),
									lg: pxToRem(32),
								},
								color: color.colWhite,
								fontWeight: 700,
								fontFeatureSettings: 'case',
							}}
						>
							%
						</UIText>
					</UIText>
					<UIText
						sx={{
							position: 'absolute',
							left: `${data.topBannerChart.ballotChart.prevBallotRate}%`,
							top: {
								xs: pxToRem(-48),
								lg: pxToRem(-50),
							},
							border: '1px solid #E0197C',
							transform: 'translateX(-50%)',
							backgroundColor: color.colWhite,
							borderRadius: pxToRem(4),
							padding: pxToRem(8),
							fontWeight: 700,
							fontSize: {
								xs: pxToRem(12),
								lg: pxToRem(14),
							},
							lineHeight: {
								xs: pxToRem(12),
								lg: pxToRem(14),
							},
							whiteSpace: 'nowrap',
							'&::after': {
								content: '""',
								position: 'absolute',
								left: '50%',
								top: {
									xs: pxToRem(28),
									lg: pxToRem(30),
								},
								transform: 'translateX(-50%)',
								width: pxToRem(2),
								height: {
									xs: pxToRem(79),
									lg: pxToRem(103),
								},
								backgroundColor: '#E0197C',
							},
							'&::before': {
								content: '""',
								position: 'absolute',
								width: pxToRem(8),
								height: pxToRem(8),
								backgroundColor: '#E0197C',
								borderRadius: '50%',
								left: '50%',
								top: {
									xs: pxToRem(39),
									lg: pxToRem(41),
								},
								transform: 'translateX(-50%)',
							},
						}}
					>
						지난 대선{' '}
						<UIText
							sx={{
								fontSize: {
									xs: pxToRem(12),
									lg: pxToRem(14),
								},
								lineHeight: {
									xs: pxToRem(12),
									lg: pxToRem(14),
								},
								color: '#E0197C',
								fontWeight: 700,
							}}
						>
							{data.topBannerChart.ballotChart.prevBallotRate}%
						</UIText>
					</UIText>
				</Box>
				<UIFlex
					flexDirection="row"
					justifyContent="flex-end"
					gap={pxToRem(2)}
					sx={{
						marginTop: {
							xs: pxToRem(13),
							lg: pxToRem(17),
						},
						fontSize: pxToRem(14),
						lineHeight: pxToRem(14),
						color: '#082B4F',
						marginRight: {
							xs: pxToRem(16),
							md: 0,
						},
					}}
				>
					투표율 자세히 보기
					<Box sx={{ display: 'flex', alignItems: 'center', marginTop: pxToRem(-1) }}>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="16"
							height="16"
							viewBox="0 0 16 16"
							fill="none"
						>
							<path d="M6 12L10 8L6 4" stroke="#242424" strokeWidth="1.5" />
						</svg>
					</Box>
				</UIFlex>
			</Box>
		</>
	)
}

export default BallotBanner
